/**
 * A Google AdSense position ("Pubblicità"). Renders nothing unless `/ads.json` enables AdSense with a
 * real publisher id and a unit for this position AND the user accepted advertising cookies
 * (AdsConsentNotice). No ads from anyone else, no fallback.
 *
 * - The box keeps the slot's aspect ratio while the ad loads (no jump of what is around it). The slots
 *   are always last in their column, so nothing above them moves when one appears or collapses.
 * - If adsbygoogle.js is blocked (ad blocker, network) or the unit is still empty after ~3 s (or Google
 *   marks it "unfilled"), the slot collapses: no empty box.
 * - `media`: render only when the media query matches, so hidden slots request nothing.
 * - Development only: `?adslots` in the URL shows neutral placeholders with the reserved sizes.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  AD_SLOTS,
  getAdConsent,
  loadAds,
  loadAdsenseScript,
  setAdConsent,
  subscribeAdConsent,
  unitFor,
  usesAdsense,
  type AdSlotId,
  type AdsConfig,
  type AdsenseUnit,
} from '../ads/ads';
import { Notice } from './Notice';

/** How long an AdSense unit may stay empty before the slot collapses. */
export const ADSENSE_FILL_TIMEOUT_MS = 3000;

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

export function useAdsConfig(): AdsConfig | null {
  const [cfg, setCfg] = useState<AdsConfig | null>(null);
  useEffect(() => {
    let alive = true;
    void loadAds().then((c) => {
      if (alive) setCfg(c);
    });
    return () => {
      alive = false;
    };
  }, []);
  return cfg;
}

export function useAdConsent() {
  return useSyncExternalStore(subscribeAdConsent, getAdConsent, () => null);
}

function useMediaQuery(query: string | undefined): boolean {
  return useSyncExternalStore(
    (cb) => {
      if (!query || typeof window.matchMedia !== 'function') return () => {};
      const mq = window.matchMedia(query);
      mq.addEventListener('change', cb);
      return () => mq.removeEventListener('change', cb);
    },
    () => (!query || typeof window.matchMedia !== 'function' ? true : window.matchMedia(query).matches),
    () => true,
  );
}

/** Dev-only preview of the reserved boxes (`?adslots`): never in production builds. */
function devPreview(): boolean {
  return !!import.meta.env?.DEV && typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('adslots');
}

/** One AdSense unit: <ins class="adsbygoogle"> + a single push({}). Calls onFail if blocked or empty. */
function AdsenseCreative({ unit, onFail }: { unit: AdsenseUnit; onFail: () => void }) {
  const ref = useRef<HTMLModElement>(null);
  const failRef = useRef(onFail);
  failRef.current = onFail;

  useEffect(() => {
    const ins = ref.current;
    if (!ins) return;
    let done = false;
    const fail = () => {
      if (!done) {
        done = true;
        failRef.current();
      }
    };
    loadAdsenseScript(unit.client).catch(fail);
    // one push per <ins>, even when React StrictMode re-runs effects
    if (!ins.dataset.pushed) {
      ins.dataset.pushed = '1';
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {
        fail();
      }
    }
    const t = window.setTimeout(() => {
      const status = ins.getAttribute('data-ad-status');
      const filled = status === 'filled' || (status !== 'unfilled' && !!ins.querySelector('iframe'));
      if (!filled) fail();
    }, ADSENSE_FILL_TIMEOUT_MS);
    return () => {
      done = true;
      window.clearTimeout(t);
    };
  }, [unit.client, unit.slot]);

  return (
    <ins
      ref={ref}
      className="adsbygoogle ad-adsense"
      style={{ display: 'block', width: '100%', height: '100%' }}
      data-ad-client={unit.client}
      data-ad-slot={unit.slot}
      data-ad-format={unit.format}
      data-full-width-responsive="false"
      data-testid="ad-adsense"
    />
  );
}

export function AdSlot({ id, media, className }: { id: AdSlotId; media?: string; className?: string }) {
  const cfg = useAdsConfig();
  const consent = useAdConsent();
  const matches = useMediaQuery(media);
  const [failed, setFailed] = useState(false);
  const unit = unitFor(cfg, id);
  const preview = devPreview();

  if (!matches) return null;
  const live = !!unit && consent === 'granted' && !failed;
  if (!live && !preview) return null;
  const { width, height } = AD_SLOTS[id];

  return (
    <aside
      className={`glass ad-slot${className ? ` ${className}` : ''}`}
      aria-label="Pubblicità"
      data-testid={`ad-slot-${id}`}
      data-slot={id}
      style={{ ['--ad-w' as string]: `${width}px`, ['--ad-ratio' as string]: `${width} / ${height}` }}
    >
      <div className="ad-head">
        <span className="ad-label" aria-hidden>
          Pubblicità
        </span>
        {live ? (
          <button type="button" className="ad-prefs" onClick={() => setAdConsent(null)} data-testid="ad-consent-reset">
            Preferenze annunci
          </button>
        ) : null}
      </div>
      <div className="ad-frame">
        {live ? (
          <AdsenseCreative key={`${unit.client}/${unit.slot}`} unit={unit} onFail={() => setFailed(true)} />
        ) : (
          <div className="ad-placeholder" data-testid="ad-placeholder">
            AdSense · {id} · {width}×{height}
            <span>anteprima di sviluppo</span>
          </div>
        )}
      </div>
    </aside>
  );
}

/**
 * Minimal advertising-consent notice: shown only when AdSense is configured and the user has not chosen
 * yet. Nothing from Google loads before "Accetta". (For the EEA in production a Google-certified CMP is
 * required: see web/public/brand/ADS.md.)
 */
export function AdsConsentNotice() {
  const cfg = useAdsConfig();
  const consent = useAdConsent();
  if (!usesAdsense(cfg) || consent !== null) return null;
  return (
    <Notice
      tone="info"
      title="Annunci e cookie"
      testId="ads-consent"
      action={
        <>
          <button type="button" className="btn btn-small" onClick={() => setAdConsent('granted')} data-testid="ads-consent-accept">
            Accetta
          </button>
          <button type="button" className="btn btn-small" onClick={() => setAdConsent('denied')} data-testid="ads-consent-deny">
            Rifiuta
          </button>
        </>
      }
    >
      Titanet Voice è gratuita grazie agli annunci. Se accetti, Google AdSense può usare cookie per mostrarli; se rifiuti non vedi
      annunci e Google non viene contattato. Puoi cambiare idea in Impostazioni → Aspetto.
    </Notice>
  );
}

/** Settings → Aspetto: change the advertising-cookie choice at any time (only when AdSense is configured). */
export function AdsConsentSetting() {
  const cfg = useAdsConfig();
  const consent = useAdConsent();
  if (!usesAdsense(cfg)) return null;
  const state = consent === 'granted' ? 'accettati' : consent === 'denied' ? 'rifiutati' : 'non hai ancora scelto';
  return (
    <div className="field field-row" data-testid="ads-consent-setting">
      <div>
        <span className="field-label">Annunci (Google AdSense)</span>
        <p className="field-hint">
          Cookie pubblicitari: <strong>{state}</strong>. Gli annunci finanziano il server; se rifiuti non ne vedi nessuno. Dopo un
          rifiuto Google non viene più contattato dalla prossima apertura della pagina; i cookie già salvati si cancellano dai dati
          del sito nel browser.
        </p>
      </div>
      <div className="ads-consent-buttons">
        <button type="button" className="btn btn-small" disabled={consent === 'granted'} onClick={() => setAdConsent('granted')} data-testid="ads-setting-accept">
          Accetta
        </button>
        <button type="button" className="btn btn-small" disabled={consent === 'denied'} onClick={() => setAdConsent('denied')} data-testid="ads-setting-deny">
          Rifiuta
        </button>
      </div>
    </div>
  );
}
